"use client";

import { CheckCircle2, Loader2, Pencil, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  getShopReviews,
  removeMyShopReview,
  reportShopReview,
  type ShopReviewBundle,
  toggleShopReviewHelpful,
  upsertShopReview,
} from "@/actions/shop-review";
import {
  EMPTY_MEDIA_UPLOAD_STATE,
  ImageUpload,
  type MediaUploadState,
} from "@/components/image-upload";
import {
  ReviewStarInput,
  ReviewStars,
  toWholeStarRating,
} from "@/components/reviews/review-stars";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  type MediaAssetSlim,
  mediaPreviewUrlFromAsset,
  toMediaAssetSlimForWrite,
} from "@/lib/media";

export function ShopReviewsSection({ shopItemId }: { shopItemId: number }) {
  const [bundle, setBundle] = useState<ShopReviewBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [media, setMedia] = useState<MediaAssetSlim[]>([]);
  const [uploadState, setUploadState] = useState<MediaUploadState>(
    EMPTY_MEDIA_UPLOAD_STATE,
  );
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const result = await getShopReviews(shopItemId);
    if (result.ok) setBundle(result.data);
    setLoading(false);
  }, [shopItemId]);

  useEffect(() => {
    void load();
  }, [load]);

  const beginEditing = () => {
    const current = bundle?.currentReview;
    setRating(toWholeStarRating(current?.rating ?? 0));
    setTitle(current?.title ?? "");
    setBody(current?.body ?? "");
    setMedia(current?.media ?? []);
    setEditing(true);
  };

  const save = async () => {
    if (rating <= 0) return toast.error("Select a rating.");
    if (uploadState.isUploading || uploadState.isCropping) {
      return toast.error("Wait for media uploads to finish.");
    }
    setSaving(true);
    const result = await upsertShopReview({
      shopItemId,
      rating,
      title: title.trim() || null,
      body: body.trim() || null,
      media,
    });
    setSaving(false);
    if (!result.ok) return toast.error(result.message);
    toast.success(
      bundle?.currentReview ? "Review updated" : "Review published",
    );
    setEditing(false);
    await load();
  };

  const remove = async () => {
    const result = await removeMyShopReview({ shopItemId });
    if (!result.ok) return toast.error(result.message);
    toast.success("Review removed");
    setEditing(false);
    await load();
  };

  if (loading) {
    return (
      <div className="mt-6 flex items-center gap-2 border-t border-border pt-5 text-sm text-text-tertiary">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading reviews
      </div>
    );
  }
  if (!bundle) return null;

  return (
    <section className="mt-6 border-t border-border pt-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-base font-semibold text-text-primary">Reviews</h3>
          {bundle.reviewCount > 0 ? (
            <div className="mt-1 flex items-center gap-2">
              <ReviewStars rating={bundle.averageRating} size="sm" showValue />
              <span className="text-xs text-text-tertiary">
                {bundle.reviewCount} verified
              </span>
            </div>
          ) : (
            <p className="mt-1 text-sm text-text-tertiary">No reviews yet.</p>
          )}
        </div>
        {bundle.canReview && !editing ? (
          <Button size="sm" variant="outline" onClick={beginEditing}>
            <Pencil className="h-3.5 w-3.5" />
            {bundle.currentReview ? "Edit" : "Write a review"}
          </Button>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-4 space-y-4 rounded-xl border border-border bg-surface/60 p-4">
          <p className="text-sm text-text-secondary">
            Only the overall rating is required. Everything else is optional.
          </p>
          <p className="text-sm font-medium">Overall rating · Required</p>
          <ReviewStarInput value={rating} onChange={setRating} />
          <p className="text-sm font-medium">Title · Optional</p>
          <Input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={120}
            placeholder="Sum it up"
          />
          <p className="text-sm font-medium">Written review · Optional</p>
          <Textarea
            value={body}
            onChange={(event) => setBody(event.target.value)}
            maxLength={3000}
            rows={4}
            placeholder="How was the quality, fit, and fulfillment?"
          />
          <p className="text-sm font-medium">Photos and videos · Optional</p>
          <ImageUpload
            maxImages={4}
            acceptVideos
            initialImages={bundle.currentReview?.media ?? []}
            uploadScope={{
              domain: "reviews",
              slot: "shop-review-media",
              entityRef: String(shopItemId),
            }}
            onUploadStateChange={setUploadState}
            onImagesChange={(items) => {
              setMedia(
                items
                  .filter(
                    (item) => item.url && !item.error && !item.isUploading,
                  )
                  .sort((a, b) => a.order - b.order)
                  .map((item, index) => toMediaAssetSlimForWrite(item, index))
                  .filter((item): item is MediaAssetSlim => item !== null),
              );
            }}
          />
          <div className="flex items-center justify-between gap-3">
            {bundle.currentReview ? (
              <Button size="sm" variant="ghost" onClick={() => void remove()}>
                <Trash2 className="h-4 w-4" /> Remove
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
              <Button size="sm" disabled={saving} onClick={() => void save()}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Publish
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {bundle.reviews.length > 0 ? (
        <div className="mt-4 space-y-3">
          {bundle.reviews.slice(0, 5).map((review) => (
            <article
              key={review.publicId}
              className="rounded-xl bg-surface/60 p-3"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-text-primary">
                    {review.reviewerName}
                  </p>
                  <p className="mt-0.5 flex items-center gap-1 text-[11px] text-success">
                    <CheckCircle2 className="h-3 w-3" /> Verified purchase
                  </p>
                </div>
                <ReviewStars rating={review.rating} size="sm" />
              </div>
              {review.title ? (
                <h4 className="mt-2 text-sm font-semibold text-text-primary">
                  {review.title}
                </h4>
              ) : null}
              {review.body ? (
                <p className="mt-1 text-sm leading-relaxed whitespace-pre-line text-text-secondary">
                  {review.body}
                </p>
              ) : null}
              {review.media.length > 0 ? (
                <div className="mt-3 flex gap-2 overflow-x-auto">
                  {review.media.map((asset) => {
                    const preview = mediaPreviewUrlFromAsset(asset);
                    return preview ? (
                      // biome-ignore lint/performance/noImgElement: compact user media preview uses the direct signed asset URL
                      <img
                        key={asset.id}
                        src={preview}
                        alt="Product review"
                        className="h-20 w-16 shrink-0 rounded-lg object-cover"
                      />
                    ) : null;
                  })}
                </div>
              ) : null}
              {review.sellerResponse ? (
                <div className="mt-3 border-l-2 border-border pl-3">
                  <p className="text-xs font-medium text-text-primary">
                    Seller response
                  </p>
                  <p className="mt-1 text-sm text-text-secondary">
                    {review.sellerResponse}
                  </p>
                </div>
              ) : null}
              {!review.isMine ? (
                <div className="mt-3 flex gap-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const result = await toggleShopReviewHelpful(
                        review.publicId,
                      );
                      if (!result.ok) return toast.error(result.message);
                      toast.success(
                        result.data.helpful
                          ? "Marked helpful"
                          : "Helpful vote removed",
                      );
                    }}
                  >
                    Helpful
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      const result = await reportShopReview({
                        publicId: review.publicId,
                        reason: "other",
                        details: null,
                      });
                      if (!result.ok) return toast.error(result.message);
                      toast.success("Review reported for moderation");
                    }}
                  >
                    Report
                  </Button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}
